import { formatNullableCurrencyDetailed, formatIndianAmountFixed } from "../../utils/currency.utils";
import { calculateWeightedAverage } from "../../utils/calculation";

/**
 * Color map for portfolio types
 * Add more as backend sends new types
 */
const PORTFOLIO_COLOR_MAP = {
    "Mutual Funds": "#7B5EA7",
    "Stocks": "#8C6DB1",
    "ETF": "#9D7CBB",
    "INVIT": "#AE8BC5",
    "REIT": "#BF9ACF",
    "AIF": "#D0A9D9",
    "Fixed Deposit": "#E1B8E3",
    "NPS": "#F2C7ED",
};

/**
 * Parser for Investment Portfolio API response.
 * Converts API schema to UI expected format.
 *
 * @param {Object} data - API response result object
 * @param {Object} portfolioSummary - Portfolio summary object (optional)
 * @returns {Object|null} Parsed portfolio data for UI
 */
export const parseInvestmentPortfolio = (data, portfolioSummary = null) => {
    if (!data) return null;

    const portfolioValue = Number(String(portfolioSummary?.currentValue || 0).replace(/,/g, '')) || 0;
    const aaTotalValue = Number(String(data?.totalValue || 0).replace(/,/g, '')) || 0;
    const totalValue = aaTotalValue + portfolioValue;
    const asOnDate = data?.asOnDate ?? null;
    const portfolios = [...(data?.portfolios ?? [])];

    // Ensure Mutual Funds category exists if Inxits has value
    const hasMutualFunds = portfolios.some(p => p?.type === "Mutual Funds");
    if (!hasMutualFunds && portfolioValue > 0) {
        portfolios.push({
            type: "Mutual Funds",
            value: 0,
            count: 0,
            growth: Number(portfolioSummary?.oneDayReturnsPercentage || 0),
            percent: totalValue > 0 ? (portfolioValue / totalValue) * 100 : 0
        });
    }

    // Format date: "2026-03-13T10:45:32+05:30" → "13 March 2026; 10:45:32 AM IST"
    const formatAsOnDate = (isoString) => {
        if (!isoString) return "-";
        try {
            const date = new Date(isoString);
            const formatted = date.toLocaleString("en-IN", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZone: "Asia/Kolkata",
            });
            // Handle both Windows "at" and macOS/other "," separators
            return formatted.replace(/, | at /g, "; ") + " IST";
        } catch {
            return "-";
        }
    };

    // Map portfolios → breakdown (for PortfolioProgressBar)
    const breakdown = portfolios
        .filter(item => !["Recurring Deposit", "RD", "FD"].includes(item?.type))
        .map((item, index) => {
            const isMutualFund = item?.type === "Mutual Funds";
            const aaItemValue = Number(String(item?.value || 0).replace(/,/g, '')) || 0;
            const combinedItemValue = isMutualFund ? (aaItemValue + portfolioValue) : aaItemValue;
            
            // Increment count for Mutual Funds if Inxits has value
            const finalCount = (isMutualFund && portfolioValue > 0) ? (item?.count ?? 0) + 1 : (item?.count ?? 0);

            const combinedGrowth = Number(item?.growth ?? item?.dayChangePercent ?? 0);

            const isGrowthPositive = combinedGrowth >= 0;

            return {
                id: index,
                label: (item?.type === "etf" || item?.type === "Etf") ? "ETF" : (item?.type === "stocks" || item?.type === "Stocks") ? "STOCKS" : (item?.type ?? "Unknown"),
                percentage: item?.percent != null ? Number(item.percent) : (totalValue > 0 ? parseFloat(((combinedItemValue / totalValue) * 100).toFixed(2)) : 0),
                color: PORTFOLIO_COLOR_MAP[item?.type] ?? "#7B5EA7",
                // extra fields for PortfolioPanel
                count: `${finalCount} ${["NPS", "AIF", "Fixed Deposit"].includes(item?.type) ? (finalCount === 1 ? "holding" : "holdings") : (finalCount === 1 ? "Broker" : "Brokers")}`,
                value: formatIndianAmountFixed(combinedItemValue),
                rawValue: combinedItemValue,
                rawCount: finalCount,
                growth: combinedGrowth.toFixed(2),
                isPositive: isGrowthPositive,
            };
        })
        .filter((item) => item.rawCount > 0); // skip categories with zero count

    return {
        title: "Total Investments",
        totalValue: formatNullableCurrencyDetailed(totalValue),
        asOfDate: `as on date ${formatAsOnDate(asOnDate)}`,
        breakdown,
    };
};